import type { ReactNode } from "react";

import logo from "../../../assets/images/logo.svg";
import { useLanguage } from "../../../context/LanguageProvider";
import { toFarsiNumber } from "../../../shared/utils/commonUtils";

import type { BlogPost } from "../types";
import { formatRelativeTime } from "../utils/date";
import BlogShareLinks from "./BlogShareLinks";

interface InfoBlockProps {
    label: string;
    children: ReactNode;
}

// One "label over value" block of the sidebar
const InfoBlock = ({ label, children }: InfoBlockProps) => (
    <div className="p-5 lg:p-3 border-t border-gray-300">
        <div className="flex justify-center mb-4 items-center">
            <span className="opacity-80 text-lg md:text-2xl lg:text-md font-bold">{label}</span>
        </div>
        <div className="flex justify-center items-center text-lg md:text-2xl lg:text-md">
            {children}
        </div>
    </div>
);

interface BlogInfoSidebarProps {
    post: BlogPost;
    url: string; // Public URL used by the share buttons
}

// Brand, last update, read time and share buttons
const BlogInfoSidebar = ({ post, url }: BlogInfoSidebarProps) => {
    const { lang, t } = useLanguage();

    const updatedAt = formatRelativeTime(post.updated_at, lang);
    const readTime = post.read_time ?? 0;

    return (
        <aside className="w-full lg:w-1/5 sticky top-24 h-fit">
            <div className="flex items-center justify-center p-5 lg:p-3">
                <img src={logo} alt="" className="h-12 ml-3" />
                <span className="text-lg md:text-2xl lg:text-md font-bold opacity-80">
                    {t.blog.team}
                </span>
            </div>

            <InfoBlock label={t.blog.lastUpdated}>{updatedAt}</InfoBlock>

            <InfoBlock label={t.blog.readTime}>
                {lang === "fa" ? toFarsiNumber(readTime) : readTime} {t.blog.minutes}
            </InfoBlock>

            <div className="p-5 lg:p-3 border-t border-gray-300 mt-5 lg:mt-0">
                <div className="flex justify-center mb-5 items-center">
                    <span className="text-lg md:text-2xl lg:text-md opacity-80 font-bold">
                        {t.blog.share}
                    </span>
                </div>
                <BlogShareLinks url={url} />
            </div>
        </aside>
    );
};

export default BlogInfoSidebar;